<template>
  <main class="tictactoe-page" :class="appStore.theme">
    <section class="game-stage">
      <aside v-if="gamePlayers[0]" class="player-card x" :style="{ '--player-colour': gamePlayers[0].playerColour }">
        <span class="symbol">{{ gamePlayers[0].symbol }}</span>
        <span class="connection-dot" :class="{ offline: gamePlayers[0].connected === false, away: gamePlayers[0].connected !== false && !gamePlayers[0].inGame }" :style="{ backgroundColor: gamePlayers[0].connected === false ? '#ff4d5e' : gamePlayers[0].inGame === true ? '#40dc74' : '#ffad32' }" />
        <span class="player-name">{{ playerName(gamePlayers[0]) }}</span>
        <span class="controller-hint">{{ t('tic_tac_toe.uses_symbol', { symbol: gamePlayers[0].symbol }) }}</span>
      </aside>
      <section class="game-content">
        <h1>{{ t('tic_tac_toe.title') }}</h1>
        <p class="instruction">{{ t('tic_tac_toe.instruction') }}</p>
        <p v-if="!winner" class="turn-message">{{ turnMessage }}</p>
        <div class="board" aria-label="Tic Tac Toe board">
          <button v-for="(cell, index) in board" :key="index" class="cell" :class="cell ? cell.toLowerCase() : ''" :aria-label="t('tic_tac_toe.square', { square: index + 1 })" disabled>
            {{ cell || index + 1 }}
          </button>
        </div>
      </section>
      <aside v-if="gamePlayers[1]" class="player-card o" :style="{ '--player-colour': gamePlayers[1].playerColour }">
        <span class="symbol">{{ gamePlayers[1].symbol }}</span>
        <span class="connection-dot" :class="{ offline: gamePlayers[1].connected === false, away: gamePlayers[1].connected !== false && !gamePlayers[1].inGame }" :style="{ backgroundColor: gamePlayers[1].connected === false ? '#ff4d5e' : gamePlayers[1].inGame === true ? '#40dc74' : '#ffad32' }" />
        <span class="player-name">{{ playerName(gamePlayers[1]) }}</span>
        <span class="controller-hint">{{ t('tic_tac_toe.uses_symbol', { symbol: gamePlayers[1].symbol }) }}</span>
      </aside>
    </section>
    <section v-if="winner" class="result-overlay" :class="{ draw: winner === 'draw' }">
      <div v-if="winner !== 'draw'" class="fireworks" aria-hidden="true">
        <i v-for="spark in 48" :key="spark" :style="confettiStyle(spark)"></i>
      </div>
      <div class="result-card">
        <div class="result-icon">{{ winner === 'draw' ? '🤝' : '🏆' }}</div>
        <h2>{{ resultTitle }}</h2>
        <p>{{ winner === 'draw' ? t('tic_tac_toe.draw_subtitle') : t('tic_tac_toe.win_subtitle') }}</p>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useAppStore } from '@/stores/appStore'
import eventBus from '@/plugins/eventBus'
import { useWebsocketStore } from '@/stores/websocketStore'
import { useI18n } from 'vue-i18n'
import { soundManager, SOUNDS } from '@/services/soundManager'

defineOptions({ name: 'TicTacToePage' })

const appStore = useAppStore()
const socketStore = useWebsocketStore()
const { t } = useI18n()
const board = ref(Array(9).fill(''))
const initialStarterIndex = Number(appStore.room?.game?.roundStarterIndex) === 1 ? 1 : 0
const turnIndex = ref(initialStarterIndex)
const roundStarterIndex = ref(initialStarterIndex)
const winner = ref('')
const gamePlayers = computed(() => (appStore.room?.game?.players || []).slice(0, 2).map((player, index) => ({ ...player, symbol: index === 0 ? 'X' : 'O' })))
const activePlayer = computed(() => gamePlayers.value[turnIndex.value])
function playerName(player) {
  return player?.displayName || player?.name || player?.email || t('tic_tac_toe.player')
}
const turnMessage = computed(() => {
  if (winner.value === 'draw') return t('tic_tac_toe.draw')
  if (winner.value) return t('tic_tac_toe.symbol_wins', { symbol: winner.value })
  if (!activePlayer.value) return t('tic_tac_toe.waiting')
  return t('tic_tac_toe.turn', { player: playerName(activePlayer.value), symbol: activePlayer.value.symbol })
})
const resultTitle = computed(() => {
  if (winner.value === 'draw') return t('tic_tac_toe.draw')
  const player = gamePlayers.value.find((candidate) => candidate.symbol === winner.value)
  return t('tic_tac_toe.player_wins', { player: playerName(player) })
})

function getWinningSymbol() {
  const lines = [[0, 1, 2], [3, 4, 5], [6, 7, 8], [0, 3, 6], [1, 4, 7], [2, 5, 8], [0, 4, 8], [2, 4, 6]]
  for (const [a, b, c] of lines) if (board.value[a] && board.value[a] === board.value[b] && board.value[a] === board.value[c]) return board.value[a]
  return ''
}

function handleControl(control) {
  const square = Number(control?.value)
  if (control?.type !== 'number' || square < 1 || square > 9 || winner.value) return
  const playerIndex = gamePlayers.value.findIndex((player) => player.id === control.userId)
  if (playerIndex === -1 || playerIndex !== turnIndex.value || board.value[square - 1]) return
  board.value[square - 1] = gamePlayers.value[playerIndex].symbol
  soundManager.play(SOUNDS.selection)
  const winningSymbol = getWinningSymbol()
  if (winningSymbol) finishGame(winningSymbol)
  else if (board.value.every(Boolean)) finishGame('draw')
  else turnIndex.value = turnIndex.value === 0 ? 1 : 0
}

function finishGame(result) {
  soundManager.stopAll(); soundManager.play(result === 'draw' ? SOUNDS.gameDraw : SOUNDS.winner)
  winner.value = result
  socketStore.send({ event: 'finished', data: { gameId: appStore.game?.id, result } })
}
function confettiStyle(spark) {
  return {
    '--x': `${(spark * 37) % 100}%`,
    '--delay': `${(spark % 12) * -0.18}s`,
    '--drift': `${((spark * 29) % 180) - 90}px`,
    '--colour': `hsl(${(spark * 43) % 360}, 90%, 62%)`,
  }
}

function restartRound() {
  board.value = Array(9).fill('')
  winner.value = ''
  roundStarterIndex.value = Number(appStore.room?.game?.roundStarterIndex) === 1 ? 1 : 0
  turnIndex.value = roundStarterIndex.value
}

onMounted(() => {
  eventBus.on('game-control', handleControl)
  eventBus.on('restart-round', restartRound)
  socketStore.send({ event: 'playing', data: { gameId: appStore.game.id } })
})
onBeforeUnmount(() => {
  soundManager.stopAll()
  eventBus.off('game-control', handleControl)
  eventBus.off('restart-round', restartRound)
})
</script>

<style scoped>
.tictactoe-page { height: 100vh; padding: 2vh 3vw; box-sizing: border-box; overflow: hidden; background: #101218; color: white; }
.tictactoe-page.light { background: #f5f6fa; color: #1d2433; }
.game-stage { height: 100%; display: grid; grid-template-columns: minmax(150px, 1fr) minmax(420px, auto) minmax(150px, 1fr); align-items: center; gap: 2vw; }
.player-card { position: relative; display: grid; grid-template-columns: auto 1fr; column-gap: 1vw; align-items: center; padding: 2vh 1.5vw; border-radius: 18px; background: #202530; border: 3px solid #566070; }
.player-card.x { border-color: var(--player-colour, #58b7ff); }.player-card.o { border-color: var(--player-colour, #ff6b9d); }
.symbol { grid-row: span 2; font-size: clamp(2.4rem, 7vh, 4rem); font-weight: 900; }.x .symbol { color: #58b7ff; }.o .symbol { color: #ff6b9d; }
.player-name { font-size: clamp(1rem, 2.7vh, 1.6rem); font-weight: 700; }.connection-dot { position: absolute; top: .65rem; right: .65rem; width: .8rem; height: .8rem; border-radius: 50%; background: #40dc74; box-shadow: 0 0 .6rem #40dc74; }.connection-dot.away { background: #ffad32; box-shadow: 0 0 .6rem #ffad32; }.connection-dot.offline { background: #77808f; box-shadow: none; }.controller-hint { opacity: .72; font-size: clamp(.8rem, 1.8vh, 1.05rem); }
.game-content { min-height: 0; display: flex; flex-direction: column; align-items: center; text-align: center; }.game-content h1 { margin: 0; font-size: clamp(1.8rem, 4.5vh, 3rem); }.instruction { margin: 0; opacity: .75; font-size: clamp(.8rem, 2vh, 1.2rem); }.turn-message { margin: .5vh 0; min-height: 1.5rem; font-size: clamp(1rem, 2.6vh, 1.55rem); font-weight: 700; }
.board { display: grid; grid-template-columns: repeat(3, min(25vh, 15vw, 170px)); justify-content: center; gap: min(1.4vh, 10px); }
.cell { aspect-ratio: 1; border: 0; border-radius: 14px; background: #252b38; color: #8290a5; font-size: 3rem; font-weight: 800; }.cell.x { color: #58b7ff; }.cell.o { color: #ff6b9d; }
.light .player-card { background: white; }.light .cell { background: white; box-shadow: 0 4px 16px #1d243322; }
.result-overlay { position: fixed; inset: 0; z-index: 50; display: flex; align-items: center; justify-content: center; overflow: hidden; background: rgba(4, 8, 20, .82); }
.result-card { position: relative; z-index: 2; width: min(70vw, 680px); margin: auto; padding: clamp(1.5rem, 5vh, 4rem); border: 3px solid #ffd166; border-radius: 28px; background: linear-gradient(145deg, #222b4a, #101522); text-align: center; box-shadow: 0 1rem 5rem #000; animation: result-pop .45s ease-out both; }.result-overlay.draw .result-card { border-color: #8ee3ef; background: linear-gradient(145deg, #24464d, #13272c); }
.result-icon { font-size: clamp(3.5rem, 11vh, 7rem); }.result-card h2 { margin: .2em 0; font-size: clamp(2rem, 7vh, 5rem); color: #fff7b2; }.result-card p { margin: 0; font-size: clamp(1rem, 2.4vh, 1.6rem); opacity: .85; }
.fireworks { position: absolute; inset: 0; }.fireworks i { position: absolute; left: var(--x); top: -5vh; width: .65rem; height: 1rem; border-radius: .18rem; background: var(--colour); animation: confetti-fall 3s linear infinite; animation-delay: var(--delay); }@keyframes confetti-fall { from { transform: translateX(0) rotate(0deg); opacity: 1; } to { transform: translateX(var(--drift)) translateY(115vh) rotate(780deg); opacity: .15; } }
@keyframes result-pop { from { transform: scale(.7); opacity: 0; } to { transform: scale(1); opacity: 1; } } @keyframes burst { from { transform: rotate(var(--angle)) translateY(0); opacity: 1; } to { transform: rotate(var(--angle)) translateY(min(-48vh, -360px)); opacity: 0; } }
.connection-dot.offline { background: #ff4d5e; box-shadow: 0 0 .5rem #ff4d5e; }
</style>
